import React, { useMemo } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import LoadingEllipsis from "../../../components/Loading/LoadingEllipsis";
import { DefaultTable } from "../../../components/Tables/subs";
import { NoDataMsg } from "../../../components/AppMessages";


interface Props {
    queryFn: () => Record<string, any>;
    queryKey: string;
    title: string;
}

const OverviewOfSourceDomainsTable = withRouter((props: RouteComponentProps & Props) => {
    const { history, queryFn, queryKey, title } = props;

    const searchWithoutDomain = useMemo(() => {
        const params = new URLSearchParams(history.location.search);
        params.delete("domain");
        params.delete("funnel");
        return params.toString();
    }, [history.location.search]);

    const { data, isFetching, refetch } = useQuery({
        queryKey: [queryKey, searchWithoutDomain],
        queryFn: () => queryFn(),
        enabled: false,
    });

    React.useEffect(() => {
        const urlDateRange = new URLSearchParams(history.location.search).get("date_range");
        if (urlDateRange) refetch();
    }, [searchWithoutDomain]);

    const domainParam = new URLSearchParams(history.location.search).get("domain");

    const filteredData = useMemo(() => {
        if (!data) return [];
        if (!domainParam || domainParam === "All") return data;
        const selectedDomains = domainParam.split(",");
        return data.filter((row: Record<string, any>) => selectedDomains.includes(row.domain));
    }, [data, domainParam]);


    const columns = useMemo(
        () => [
            {
                id: "rank",
                Header: "",
                accessor: `rank`,
                sortType: "basicCustom",
            },
            {
                id: `domain`,
                Header: `Domain`,
                accessor: `domain`,
                sortType: "alphanumericCustom",
            },
            {
                id: `share_of_citation`,
                Header: `Share of Citation`,
                accessor: `share_of_citation`,
                sortType: "alphanumericFalsyLast",
                Cell: (cell: { value: string }) => {
                    const { value } = cell;
                    return (
                        <span data-value={value}>
                            {value && value !== "0" ? `${parseFloat(value).toFixed(1)}%` : "-"}
                        </span>
                    );
                }
            },

        ],
        []
    );


    return (
        <div className="gdr_tile primary_tile_background" >
            <div className="">
                <div className="h-30 grid-col-span-6 verbatimTile">
                    <div className="title_container">
                        <h3 className='summary_title'>{title}</h3>
                    </div>

                    {isFetching ? (
                        <LoadingEllipsis isLoading={isFetching} />
                    ) :
                        filteredData?.length > 0 ?
                            (
                                <DefaultTable
                                    columns={columns}
                                    data={filteredData}
                                    id={"topicVerbatimTable"}
                                    isChildComponent={false}
                                    initialSortColId={{ id: `share_of_citation`, desc: true }}
                                    initialSortBy={[{ id: `share_of_citation`, desc: true }, { id: `rank`, desc: false }]}
                                    hiddenCols={[]}
                                    extraClass="verbatimTable"
                                    showPagination={true}
                                    pageSize={10}
                                />
                            )
                            : (<div className="no_content"><NoDataMsg /></div>)
                    }
                </div>
            </div>
        </div>
    );
});

export default OverviewOfSourceDomainsTable;